import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const segmentColorByLevel = {
  weak: 'bg-danger',
  fair: 'bg-(--meter-fair)',
  good: 'bg-(--meter-strong)',
  strong: 'bg-(--meter-strong)',
} as const;

type Props = {
  readonly password: string;
  readonly className?: string;
};

const segmentIds = ['first', 'second', 'third', 'fourth'] as const;
const segmentCount = segmentIds.length;

/**
 * A 0-4 score from length and character variety. Purely a UX hint, deliberately
 * dependency-free (no zxcvbn).
 */
const computeStrength = (password: string): number => {
  if (!password) {
    return 0;
  }

  const variety = [/[a-z]/, /[A-Z]/, /\d/, /[^\dA-Za-z]/].filter((regex) =>
    regex.test(password)
  ).length;

  const score =
    (password.length >= 8 ? 1 : 0) +
    (password.length >= 12 ? 1 : 0) +
    (variety >= 3 ? 1 : 0) +
    (variety >= 4 ? 1 : 0);

  // Floored to 1: the label already says "Weak", and an empty meter beside it read as not started.
  return Math.max(1, Math.min(score, segmentCount));
};

const strengthLevels = ['weak', 'fair', 'good', 'strong'] as const;

const StrengthMeter = ({ password, className }: Props) => {
  const { t } = useTranslation();

  const score = useMemo(() => computeStrength(password), [password]);
  const level = strengthLevels[Math.max(score - 1, 0)] ?? 'weak';
  const label = t(`input.password_strength.${level}`);

  // The live region stays mounted while the password is empty: one that mounts with its text is
  // often not announced. `absolute` while empty keeps it out of the parent's flex gap.
  return (
    <div role="status" aria-live="polite" className={classNames('empty:absolute', className)}>
      {password && (
        <div className="flex flex-col gap-1.5">
          <div className="flex w-full items-center gap-1.5">
            {segmentIds.map((segmentId, index) => (
              <div
                key={segmentId}
                className={classNames(
                  'h-1 flex-1 rounded-full transition-colors duration-200 motion-reduce:transition-none',
                  index < score ? segmentColorByLevel[level] : 'bg-default'
                )}
              />
            ))}
          </div>
          <span className="text-xs text-muted">
            {t('input.password_strength.label', { strength: label })}
          </span>
        </div>
      )}
    </div>
  );
};

export default StrengthMeter;
